import React, { FC, Fragment, ReactNode, useEffect, useRef } from 'react'
import {
  Button,
  IconX,
  Divider,
  Text,
  csx,
  Flex,
  useBreakpoint,
} from '@vtex/admin-ui'
import { getReduxStore } from '@/checkout-instore-legacy/redux/reduxStore'
import { CSSTransitionGroup } from './CSSTransitionGroup'

const topBarMobileTabletSize = 3.25
const topBarDesktopSize = 4
const globalTopMessageSize = 4.6
const RIGHT_SIDE_MODAL_DEFAULT_WIDTH = '31.25rem'

// Margin calculated so it is just bellow the topbar.
const calculateMarginTop = (topBarSize: number) => {
  return getReduxStore().getState().globalTopMessage.message
    ? `${topBarSize + globalTopMessageSize}rem`
    : `${topBarSize}rem`
}

type RightSideModalContent = {
  isOpen: boolean
  children: ReactNode
}

const RightSideModalContent = ({ isOpen, children }: RightSideModalContent) => {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen || !ref.current) {
      return
    }

    const offsetTop = ref.current.getBoundingClientRect().top
    ref.current.style.maxHeight = `calc(100% - ${offsetTop}px)`
  }, [isOpen])

  return (
    <div ref={ref} className={csx({ overflow: 'auto' })}>
      {children}
    </div>
  )
}

export type RightSideModalProps = {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  title: string
  width?: string
}

const RightSideModal: FC<RightSideModalProps> = ({
  isOpen,
  setIsOpen,
  title,
  children,
  width = RIGHT_SIDE_MODAL_DEFAULT_WIDTH,
  ...props
}) => {
  const { breakpoint } = useBreakpoint()
  const isMobileOrTablet = ['mobile', 'tablet'].includes(breakpoint)
  const topBarSize = isMobileOrTablet
    ? topBarMobileTabletSize
    : topBarDesktopSize
  const handleCloseSideBar = () => {
    setIsOpen(false)
  }

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'visible'
  }, [isOpen])

  return (
    <Fragment>
      <div
        data-testid="right-side-modal"
        className={csx({
          position: 'absolute',
          top: calculateMarginTop(topBarSize),
          right: 0,
          bottom: 0,
          left: 0,
          width: '100%',
          height: '100%',
          'z-index': 100,
          pointerEvents: isOpen ? 'auto' : 'none',
        })}
        {...props}
      >
        <CSSTransitionGroup
          transitionName="fadeSlow"
          transitionEnterTimeout={200}
          transitionLeaveTimeout={200}
        >
          {isOpen && (
            <div
              data-id="menu-overlay"
              key="menu-overlay"
              className={csx({
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                width: '100%',
                height: '100%',
                zIndex: 100,
                backgroundColor: 'rgba(0,0,0,.4)',
                pointerEvents: 'all',
              })}
              onMouseDown={() => handleCloseSideBar()}
            />
          )}
        </CSSTransitionGroup>

        <div
          className={csx({
            position: 'absolute',
            right: 0,
            zIndex: 999,
            width,
            height: '100%',
            overflow: 'hidden',
          })}
        >
          <CSSTransitionGroup
            transitionName="slideLeft"
            transitionEnterTimeout={200}
            transitionLeaveTimeout={200}
          >
            {isOpen && (
              <div
                className={csx({
                  height: '100%',
                  backgroundColor: '$primary',
                })}
              >
                <Flex
                  direction="row"
                  align="center"
                  justify="space-between"
                  className={csx({
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexDirection: 'row',
                    paddingX: '$space-6',
                    paddingY: '$space-5',
                  })}
                >
                  <Text
                    variant="pageTitle"
                    data-testid="right-side-modal-title"
                  >
                    {title}
                  </Text>
                  <Button
                    icon={<IconX title="close-about" />}
                    aria-label="close information button"
                    variant="neutralTertiary"
                    data-testid="close-right-side-modal"
                    onClick={() => handleCloseSideBar()}
                  />
                </Flex>
                <Divider />
                <RightSideModalContent isOpen={isOpen}>
                  {children}
                </RightSideModalContent>
              </div>
            )}
          </CSSTransitionGroup>
        </div>
      </div>
    </Fragment>
  )
}

export default RightSideModal
